import axios from "axios";

import Api, { API_BASE_URL } from "../services/AxiosService";
import { unwrap } from "./envelope";
import type { AuthUser } from "../types/auth-types/AuthUser";

export interface SessionResponse {
  accessToken: string;
  tokenType: "Bearer";
  expiresInSeconds: number;
  user: AuthUser;
  // Present only when the server also established a cookie session
  // (ADR-0036) — absent for external users and when the mode is off.
  refreshSession?: boolean;
  csrfToken?: string;
}

/**
 * Exchange a verified ID token (Cognito, or the platform issuer's when
 * AUTH_MODE=oidc) for a Penn JWT (ADR-0010 §3.2). The ID token (not the access
 * token) is required because the backend binds on its `email` claim. The token travels in the body to keep
 * `Authorization: Bearer <token>` free for the AxiosService interceptor
 * (which auto-attaches the Penn JWT once we have one). Response shape is
 * consumed by the existing auth slice and route guards.
 */
export async function sessionExchangeRequest(
  idToken: string,
  options: { appContext?: "web" | "admin" } = {},
): Promise<SessionResponse> {
  const res = await Api.post(
    "/v1/auth/session",
    // `appContext` is read by svc-02 on the platform-issuer path only, where one
    // OIDC client serves both surfaces and `aud` cannot tell them apart (it
    // defaults to "web" when absent). Cognito tokens ignore it, so the Cognito
    // callers keep sending the bare body.
    options.appContext
      ? { idToken, appContext: options.appContext }
      : { idToken },
    {
      // Skip the request interceptor's PennToken header — we explicitly do
      // NOT want to attach a stale Penn JWT during exchange.
      headers: { "x-skip-penn-token": "1" },
    },
  );
  return unwrap<SessionResponse>(res.data);
}

export async function logoutRequest(): Promise<void> {
  await Api.post("/v1/auth/logout").catch(() => undefined);
}

/**
 * Pre-flight the email-OTP sign-in: rejects an internal/staff account on the
 * customer app (403 `wrong-app-for-account`) BEFORE an OTP is sent, so the user
 * gets a clear "use the admin portal" message instead of an OTP form for a code
 * that never arrives. Uses a bare axios call (not the shared `Api` instance) so
 * `x-auth-scope: external` cannot be stripped by the request interceptor when a
 * stale user is still in storage. The /session gate remains authoritative.
 * Resolves for permitted (or unregistered) accounts; throws AxiosError otherwise.
 */
export async function signinPrecheckRequest(email: string): Promise<void> {
  await axios.post(
    `${API_BASE_URL}/v1/auth/signin-precheck`,
    { email },
    { headers: { "x-auth-scope": "external" }, timeout: 15_000 },
  );
}

export interface RegisterRequest {
  email: string;
  firstName: string;
  lastName: string;
  regionCode: "EU" | "NA" | "ASIA";
  departmentId?: number | null;
  phone?: string | null;
  companyName?: string | null;
  companyAddress?: string | null;
  /** Cognito sub from a completed, email-verified Amplify sign-up. */
  cognitoSub: string;
  /** SPA the user signed up on. */
  appContext: "web" | "admin";
}

export async function registerRequest(
  body: RegisterRequest,
): Promise<AuthUser> {
  const res = await Api.post("/v1/users/register", body);
  return unwrap<AuthUser>(res.data);
}

/**
 * Only the email is required. svc-03 derives internal vs external from it;
 * `regionCode` is required for an external person and defaults to the
 * caller's region for an internal one.
 */
export interface RegisterWithoutOtpRequest {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  regionCode?: "EU" | "NA" | "ASIA";
  departmentId?: number | null;
  phone?: string | null;
  companyName?: string | null;
  companyAddress?: string | null;
}

/**
 * Work-For "Registration without OTP": staff with can_work_for_others register
 * the unregistered person they are working for. svc-03 creates the Cognito
 * identity server-side (confirmed + email-verified, no email/OTP reaches the
 * person) — no Amplify sign-up happens in the browser. Authenticated: the
 * Penn JWT attaches via the AxiosService interceptor.
 */
export async function registerWithoutOtpRequest(
  body: RegisterWithoutOtpRequest,
): Promise<AuthUser> {
  const res = await Api.post("/v1/users/register-without-otp", body);
  return unwrap<AuthUser>(res.data);
}

export interface RegistrationStatus {
  registered: boolean;
}

/**
 * Pre-OTP registration gate. The sign-in page calls this BEFORE starting the
 * Cognito email-OTP challenge so an unregistered email is redirected to
 * /register instead of being shown a (fake) "OTP sent" — Cognito with
 * preventUserExistenceErrors returns a challenge for unknown emails rather than
 * throwing UserNotFoundException, so the client cannot tell registered from
 * unregistered without asking our own DB. `x-skip-penn-token` keeps the call
 * anonymous (there is no Penn session before sign-in).
 */
export async function fetchRegistrationStatus(
  email: string,
): Promise<RegistrationStatus> {
  const res = await Api.post(
    "/v1/users/registration-status",
    { email },
    { headers: { "x-skip-penn-token": "1" } },
  );
  return unwrap<RegistrationStatus>(res.data);
}

/**
 * Clean up an abandoned (unverified) registration (issue 8). Called when a
 * user leaves the OTP verify step without verifying: with the post-OTP
 * DB-write reorder, the only artifact of an abandoned sign-up is an orphaned
 * Cognito user. svc-03 removes it (guarded server-side so it never touches a
 * real, active account). Best-effort: failures are swallowed because the
 * server-side periodic sweep is the backstop. `x-skip-penn-token` keeps the
 * request anonymous (there is no Penn session mid-registration).
 */
export async function abandonRegistration(email: string): Promise<void> {
  await Api.post(
    "/v1/users/abandon-registration",
    { email },
    { headers: { "x-skip-penn-token": "1" } },
  ).catch(() => undefined);
}
